import React, { useEffect, useRef } from "react";
import PropTypes from "prop-types";
import styled from "styled-components";
import { motion, useAnimation } from "framer-motion";

import { useOnScreen } from "../../hooks";
import ContentWrapper from "../../styles/contentWrapper";

const StyledSection = styled.section`
  width: 100%;
  height: auto;
  background: ${({ theme }) => theme.colors.background};
  margin-top: 6rem;
`;

const StyledContentWrapper = styled(ContentWrapper)`
  && {
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    justify-content: center;
    padding-right: 0;
    padding-left: 0;
    @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
      padding-right: 2.5rem;
      padding-left: 2.5rem;
    }
    .section-title {
      padding-right: 2.5rem;
      padding-left: 2.5rem;
      @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
        padding-right: 0;
        padding-left: 0;
      }
    }
  }
`;

const StyledCapabilities = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 2rem;
  padding: 0 2.5rem;
  @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
    grid-template-columns: repeat(3, minmax(0, 1fr));
    padding: 0;
  }

  .capability-group {
    padding-top: 1rem;
    border-top: 0.1875rem solid ${({ theme }) => theme.colors.primary};
  }

  .group-title {
    margin: 0 0 0.625rem;
    font-size: 1.125rem;
    line-height: 1.5rem;
  }

  .group-summary {
    margin: 0;
    color: ${({ theme }) => theme.colors.subtext};
  }

  .capability-list {
    display: flex;
    flex-wrap: wrap;
    gap: 0.75rem;
    margin: 1rem 0 0;
    padding: 0;
    list-style: none;
  }

  .capability-item {
    display: inline-flex;
    align-items: center;
    padding: 0.5rem 0.875rem;
    border-radius: 999px;
    border: 0.0625rem solid ${({ theme }) => theme.colors.tertiary};
    background: ${({ theme }) => theme.colors.card};
    font-weight: 500;
  }

  .capability-emoji {
    margin-right: 0.45rem;
    font-size: 0.95rem;
    line-height: 1;
  }
`;

const StyledTechnicalExecution = styled.div`
  margin-top: 2rem;
  padding: 0 2.5rem;
  @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
    padding: 0;
  }

  .execution-header {
    display: flex;
    flex-direction: column;
    margin-bottom: 1rem;
    @media (min-width: ${({ theme }) => theme.breakpoints.sm}) {
      flex-direction: row;
      align-items: baseline;
      justify-content: space-between;
    }
  }

  .execution-title {
    margin: 0;
    font-size: 1rem;
    line-height: 1.5rem;
    text-transform: uppercase;
    letter-spacing: 0.08em;
  }

  .execution-note {
    margin: 0.5rem 0 0;
    color: ${({ theme }) => theme.colors.subtext};
    @media (min-width: ${({ theme }) => theme.breakpoints.sm}) {
      margin-top: 0;
    }
  }

  .execution-grid {
    display: grid;
    grid-template-columns: 1fr;
    gap: 0.75rem;
    @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
      grid-template-columns: repeat(3, minmax(0, 1fr));
    }
  }

  .execution-item {
    padding-top: 1rem;
    border-top: 0.0625rem solid ${({ theme }) => theme.colors.tertiary};
  }

  .execution-item-title {
    margin: 0 0 0.4rem;
    font-size: 1rem;
    line-height: 1.4rem;
  }

  .execution-item-text {
    margin: 0;
    color: ${({ theme }) => theme.colors.subtext};
  }
`;

const Interests = ({ content }) => {
  const { exports, frontmatter } = content[0].node;
  const { capabilityGroups, technicalExecution } = exports;

  const ref = useRef();
  const onScreen = useOnScreen(ref);

  const groupControls = useAnimation();
  const executionControls = useAnimation();

  useEffect(() => {
    const sequence = async () => {
      if (onScreen) {
        await groupControls.start(i => ({
          opacity: 1,
          y: 0,
          transition: { delay: i * 0.08 }
        }));
        await executionControls.start({
          opacity: 1,
          y: 0,
          transition: { delay: 0.15 }
        });
      }
    };
    sequence();
  }, [onScreen, groupControls, executionControls]);

  return (
    <StyledSection id="skills">
      <StyledContentWrapper id="interests">
        <h3 className="section-title">{frontmatter.title}</h3>
        <StyledCapabilities ref={ref}>
          {capabilityGroups.map(({ title, summary, items }, key) => (
            <motion.div
              className="capability-group"
              key={key}
              custom={key}
              initial={{ opacity: 0, y: 20 }}
              animate={groupControls}
            >
              <h4 className="group-title">{title}</h4>
              <p className="group-summary">{summary}</p>
              <ul className="capability-list">
                {items.map(({ emoji, label }) => (
                  <li className="capability-item" key={label}>
                    <span className="capability-emoji" aria-hidden="true">
                      {emoji}
                    </span>
                    {label}
                  </li>
                ))}
              </ul>
            </motion.div>
          ))}
        </StyledCapabilities>
        {/*
        <StyledTechnicalExecution>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={executionControls}
          >
            <div className="execution-header">
              <h4 className="execution-title">Technical Execution</h4>
              <p className="execution-note">
                Less about specific tools, more about what my technical range
                makes possible.
              </p>
            </div>
            <div className="execution-grid">
              {technicalExecution.map(({ title, text }) => (
                <div className="execution-item" key={title}>
                  <h5 className="execution-item-title">{title}</h5>
                  <p className="execution-item-text">{text}</p>
                </div>
              ))}
            </div>
          </motion.div>
        </StyledTechnicalExecution>
        */}
      </StyledContentWrapper>
    </StyledSection>
  );
};

Interests.propTypes = {
  content: PropTypes.arrayOf(
    PropTypes.shape({
      node: PropTypes.shape({
        exports: PropTypes.shape({
          capabilityGroups: PropTypes.array.isRequired,
          technicalExecution: PropTypes.array.isRequired
        }).isRequired,
        frontmatter: PropTypes.object.isRequired
      }).isRequired
    }).isRequired
  ).isRequired
};

export default Interests;
